import React, { useState, useEffect } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { SectionName } from "../App";

interface NavItemProps {
  section: SectionName | "privacy" | "driver-setup" | "account";
  onClick: (section: SectionName | "privacy" | "driver-setup" | "account") => void;
  children: React.ReactNode;
  isActive: boolean;
}

interface NavigationProps {
  scrollToSection: (section: SectionName) => void;
}

// Modify NavItem component to accept className
const NavItem: React.FC<NavItemProps & { className?: string }> = ({ section, onClick, children, isActive, className }) => (
  <button
    onClick={() => onClick(section)}
    className={`px-3 py-2 rounded-md text-sm font-medium cursor-pointer transition-colors duration-200 
          ${isActive ? "bg-[#2C5E3B] text-white" : `${className || "text-green-200 hover:bg-green-700/50"}`}`}
  >
    {children}
  </button>
);

const Navigation: React.FC<NavigationProps> = ({ scrollToSection }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<SectionName | "privacy" | "driver-setup" | "account">("home");

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  // const AdminDashboardItem: React.FC = () => {
  //   return (
  //     <NavItem section="dashboard" onClick={handleNavClick} isActive={activeSection === "dashboard"}>
  //       DASHBOARD
  //     </NavItem>
  //   );
  // };

  const handleNavClick = (section: SectionName | "privacy" | "driver-setup" | "account" | "dashboard") => {
    if (section === "privacy") {
      navigate("/privacy-notice");
      setActiveSection("privacy");
    } else if (section === "driver-setup") {
      navigate("/driver-setup");
      setActiveSection("driver-setup");
    } else if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        scrollToSection(section as SectionName);
        setActiveSection(section as SectionName);
      }, 100);
    } else {
      scrollToSection(section as SectionName);
      setActiveSection(section as SectionName);
    }
    setIsOpen(false);
  };

  useEffect(() => {
    if (location.pathname === "/privacy-notice") {
      setActiveSection("privacy");
    } else if (location.pathname === "/driver-setup") {
      setActiveSection("driver-setup");
    } else if (location.pathname === "/account" || location.pathname === "/login" || location.pathname === "/register") {
      setActiveSection("account");
    } else {
      const handleScroll = () => {
        const scrollPosition = window.scrollY;
        const sections = document.querySelectorAll("section");

        sections.forEach((section) => {
          const sectionTop = section.offsetTop - 64;
          const sectionBottom = sectionTop + section.offsetHeight;

          if (scrollPosition >= sectionTop && scrollPosition < sectionBottom) {
            const currentSection = section.id as SectionName;
            if (currentSection !== activeSection) {
              setActiveSection(currentSection);
            }
          }
        });
      };

      window.addEventListener("scroll", handleScroll);
      handleScroll();

      return () => window.removeEventListener("scroll", handleScroll);
    }
  }, [activeSection, location.pathname]);

  const navItems: SectionName[] = ["home", "about", "innovation", "contact"];

  return (
    <nav className="bg-gradient-to-r from-[#2C5E3B] to-[#1C3F26] fixed top-0 left-0 right-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center justify-between w-full">
            <NavLink to="/" className="flex-shrink-0">
              <img src="logo.jpg" className="h-12 w-auto object-contain" alt="EcoFeed Plastics" />
            </NavLink>

            <div className="hidden lg:block">
              <div className="ml-10 flex items-center space-x-4">
                {navItems.map((item) => (
                  <NavItem key={item} section={item} onClick={handleNavClick} isActive={activeSection === item} className="text-green-200 hover:text-white hover:bg-green-700/50">
                    {item.toUpperCase()}
                  </NavItem>
                ))}
              </div>
            </div>

            {/* Mobile menu button */}
            <div className="lg:hidden">
              <button
                onClick={toggleMenu}
                type="button"
                className="inline-flex items-center justify-center p-2 rounded-md text-green-300 
                    hover:text-white hover:bg-green-700/50 focus:outline-none focus:ring-2 
                    focus:ring-inset focus:ring-white"
                aria-controls="mobile-menu"
                aria-expanded={isOpen}
              >
                <span className="sr-only">Open main menu</span>
                {!isOpen ? (
                  <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                ) : (
                  <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile menu with eco-friendly styling */}
      <div className={`lg:hidden ${isOpen ? "block" : "hidden"}`} id="mobile-menu">
        <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 flex flex-col bg-[#2C5E3B]/90 backdrop-blur-sm">
          {navItems.map((item) => (
            <NavItem key={item} section={item} onClick={handleNavClick} isActive={activeSection === item} className="text-green-200 hover:text-white hover:bg-green-700/50">
              {item.toUpperCase()}
            </NavItem>
          ))}
        </div>
      </div>
      {/* Development Notice */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-yellow-500/90 text-black text-center p-2 lg:p-3 text-sm">
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          Our product is still under research, and this website is under development
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
